import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Download } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Label } from '@/app/components/ui/label';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import {
  useClients,
  useProjects,
  useCreateInvoice,
  useGenerateInvoiceFromTime,
} from '@/hooks/use-projects';
import { useIndustry } from '@/hooks/use-industry';
import { useOrgCurrency } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import type { InvoiceLineType } from '@/lib/types';
import { getLocalToday, addLocalDays } from '@/lib/date-utils';

const LINE_TYPES: { value: InvoiceLineType; label: string }[] = [
  { value: 'labor', label: 'Labor' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'materials', label: 'Materials' },
  { value: 'travel', label: 'Travel' },
  { value: 'per_diem', label: 'Per Diem' },
  { value: 'expense', label: 'Expense' },
  { value: 'other', label: 'Other' },
];

interface LineItemRow {
  tempId: string;
  line_type: InvoiceLineType;
  description: string;
  quantity: string;
  unit: string;
  unit_price: string;
  order: number;
  time_entry?: number;
}

function generateId() {
  return Math.random().toString(36).substring(2, 11);
}

export function CreateInvoicePage() {
  const currency = useOrgCurrency();
  const navigate = useNavigate();
  const createInvoiceMutation = useCreateInvoice();
  const { labels } = useIndustry();
  const generateFromTimeMutation = useGenerateInvoiceFromTime();

  const { data: clientsData, isLoading: clientsLoading } = useClients({ page_size: 200 });
  const { data: projectsData, isLoading: projectsLoading } = useProjects({ page_size: 200 });

  const clients = clientsData?.results || [];
  const projects = projectsData?.results || [];

  const [projectId, setProjectId] = useState<number | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [issueDate, setIssueDate] = useState(getLocalToday());
  const [dueDate, setDueDate] = useState(addLocalDays(getLocalToday(), 30));
  const [taxRate, setTaxRate] = useState('0');
  const [notes, setNotes] = useState('');
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [lineItems, setLineItems] = useState<LineItemRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Time entry import state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Auto-fill client when project is selected
  const selectedProject = projects.find((p) => p.id === projectId);
  useEffect(() => {
    if (selectedProject?.client) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setClientId(selectedProject.client);
    }
  }, [selectedProject?.client]);

  // Auto-fill due date from client payment terms
  const selectedClient = clients.find((c) => c.id === clientId);
  useEffect(() => {
    if (selectedClient?.payment_terms_days && issueDate) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDueDate(addLocalDays(issueDate, selectedClient.payment_terms_days));
    }
  }, [selectedClient?.payment_terms_days, issueDate]);

  // Filter projects by client
  const filteredProjects = clientId
    ? projects.filter((p) => p.client === clientId)
    : projects;

  const subtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + qty * price;
    }, 0);
  }, [lineItems]);

  const taxAmount = useMemo(() => {
    return subtotal * (parseFloat(taxRate) || 0) / 100;
  }, [subtotal, taxRate]);

  const total = subtotal + taxAmount;

  const retainageRate = parseFloat(selectedProject?.retainage_percent || '0') || 0;
  const retainageAmount = subtotal * retainageRate / 100;

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        tempId: generateId(),
        line_type: 'labor',
        description: '',
        quantity: '1',
        unit: 'hours',
        unit_price: '0',
        order: lineItems.length,
      },
    ]);
  };

  const removeLineItem = (tempId: string) => {
    setLineItems(lineItems.filter((li) => li.tempId !== tempId));
  };

  const updateLineItem = (tempId: string, field: keyof LineItemRow, value: string) => {
    setLineItems(
      lineItems.map((li) => (li.tempId === tempId ? { ...li, [field]: value } : li))
    );
  };

  const handleImportTimeEntries = async () => {
    if (!projectId || !dateFrom || !dateTo) {
      setError('Please select a project and date range to import time entries.');
      return;
    }
    setError(null);
    try {
      const invoice = await generateFromTimeMutation.mutateAsync({
        project_id: projectId,
        date_from: dateFrom,
        date_to: dateTo,
      });
      navigate(`/projects/invoices/${invoice.id}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to generate invoice from time entries.';
      setError(msg);
    }
  };

  const handleSubmit = async () => {
    if (!projectId || !clientId) {
      setError(`Please select a project and ${labels.client.toLowerCase()}.`);
      return;
    }
    if (lineItems.length === 0) {
      setError('Please add at least one line item.');
      return;
    }
    setError(null);

    try {
      const invoice = await createInvoiceMutation.mutateAsync({
        project: projectId,
        client: clientId,
        issue_date: issueDate,
        due_date: dueDate,
        tax_rate: taxRate,
        notes,
        payment_instructions: paymentInstructions,
        line_items: lineItems.map((li, i) => ({
          line_type: li.line_type,
          description: li.description,
          quantity: li.quantity,
          unit: li.unit,
          unit_price: li.unit_price || '0',
          order: i,
          time_entry: li.time_entry,
        })),
      });
      navigate(`/projects/invoices/${invoice.id}`);
    } catch {
      setError('Failed to create invoice.');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects/invoices')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Invoice</h1>
          <p className="text-gray-600">Create a new invoice for a project</p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Project & Client Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Project & {labels.client}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Project *</Label>
              {projectsLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select
                  value={projectId?.toString() || ''}
                  onValueChange={(v) => setProjectId(parseInt(v, 10))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a project..." />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredProjects.map((p) => (
                      <SelectItem key={p.id} value={p.id.toString()}>
                        {p.project_number} — {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>{labels.client} *</Label>
              {clientsLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select
                  value={clientId?.toString() || ''}
                  onValueChange={(v) => setClientId(parseInt(v, 10))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={`Select a ${labels.client.toLowerCase()}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Details */}
      <Card>
        <CardHeader>
          <CardTitle>Invoice Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Issue Date *</Label>
              <Input
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Tax Rate (%)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
              />
            </div>
          </div>
          {selectedClient && (
            <p className="text-sm text-gray-500">
              Payment terms: Net {selectedClient.payment_terms_days} days
            </p>
          )}
        </CardContent>
      </Card>

      {/* Import Time Entries */}
      {projectId && (
        <Card
          id="invoice-import-fields"
          data-required-validation-boundary="true"
        >
          <CardHeader>
            <CardTitle>Import Time Entries</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Import approved, billable time entries from the selected project to auto-generate line items.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div>
                <Label>Date From *</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Date To *</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  required
                />
              </div>
              <Button
                data-validate-required="true"
                data-validation-scope="invoice-import-fields"
                onClick={handleImportTimeEntries}
                disabled={generateFromTimeMutation.isPending}
              >
                <Download className="h-4 w-4 mr-2" />
                {generateFromTimeMutation.isPending ? 'Importing...' : 'Import Time Entries'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line Items */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Line Items</CardTitle>
          <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
            <Plus className="h-4 w-4 mr-1" />
            Add Item
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {lineItems.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No line items yet. Add items manually or import from time entries.
            </p>
          ) : (
            lineItems.map((item) => (
              <div
                key={item.tempId}
                className="grid grid-cols-12 gap-2 items-center border rounded-lg p-3"
                data-field-container
                data-required="true"
                data-required-empty={
                  item.description.trim() ||
                  (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0) > 0
                    ? 'false'
                    : 'true'
                }
                data-required-message="Add a description or a positive line total."
                tabIndex={-1}
              >
                <div className="col-span-12 sm:col-span-2">
                  <Select
                    value={item.line_type}
                    onValueChange={(v) => updateLineItem(item.tempId, 'line_type', v)}
                  >
                    <SelectTrigger className="text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LINE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-12 sm:col-span-4">
                  <Input
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateLineItem(item.tempId, 'description', e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div className="col-span-4 sm:col-span-1">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(item.tempId, 'quantity', e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div className="col-span-4 sm:col-span-1">
                  <Input
                    placeholder="Unit"
                    value={item.unit}
                    onChange={(e) => updateLineItem(item.tempId, 'unit', e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Unit Price"
                    value={item.unit_price}
                    onChange={(e) => updateLineItem(item.tempId, 'unit_price', e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div className="col-span-10 sm:col-span-1 text-right text-sm font-medium">
                  {formatMoney((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), currency)}
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLineItem(item.tempId)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Totals */}
      {lineItems.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-end gap-2 text-sm">
              <div className="flex gap-8">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium w-32 text-right">{formatMoney(subtotal, currency)}</span>
              </div>
              {parseFloat(taxRate) > 0 && (
                <div className="flex gap-8">
                  <span className="text-gray-600">Tax ({taxRate}%):</span>
                  <span className="font-medium w-32 text-right">{formatMoney(taxAmount, currency)}</span>
                </div>
              )}
              <div className="flex gap-8 text-lg font-bold border-t pt-2 mt-1">
                <span>Total:</span>
                <span className="w-32 text-right">{formatMoney(total, currency)}</span>
              </div>
              {retainageRate > 0 && (
                <div className="flex gap-8 text-sm text-orange-700 mt-1">
                  <span>Retainage ({retainageRate}%):</span>
                  <span className="w-32 text-right">{formatMoney(retainageAmount, currency)}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes & Payment Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Notes & Payment Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Any notes for the invoice..."
            />
          </div>
          <div>
            <Label>Payment Instructions</Label>
            <Textarea
              value={paymentInstructions}
              onChange={(e) => setPaymentInstructions(e.target.value)}
              rows={3}
              placeholder="Payment instructions (bank details, payment methods accepted, etc.)"
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/projects/invoices')}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={createInvoiceMutation.isPending}
        >
          {createInvoiceMutation.isPending ? 'Creating...' : 'Create Invoice'}
        </Button>
      </div>
    </div>
  );
}
